import type { CadBinaryPreviewContext } from "./cad";
import type { PreviewInstance } from "../types";

type LibreDwgModule = {
  Dwg_File_Type: {
    DWG: number;
  };
  LibreDwg: {
    create(filepath?: string): Promise<LibreDwgValue>;
  };
};

type LibreDwgValue = {
  dwg_read_data(fileContent: ArrayBuffer, fileType: number): number;
  dwg_bmp(data: number): DwgThumbnail | null;
  convertEx(data: number): {
    database: DwgDatabase;
    stats: {
      unknownEntityCount: number;
    };
  };
  dwg_to_svg(database: DwgDatabase): string;
  dwg_free(data: number): void;
};

type DwgThumbnail = {
  data: Uint8Array;
  type: number;
};

type DwgDatabase = {
  entities: Array<{
    isInPaperSpace?: boolean;
  }>;
  tables: {
    LAYER: {
      entries: Array<{
        off?: boolean;
        frozen?: boolean;
      }>;
    };
  };
  objects: {
    LAYOUT: Array<{
      layoutName?: string;
    }>;
  };
};

export interface LibreDwgPreviewOptions {
  /**
   * Enable the built-in LibreDWG WASM preview for DWG files.
   *
   * It is best-effort and intentionally lower priority than `binaryRenderer`.
   */
  enabled?: boolean;
  /**
   * Public URL that contains libredwg-web.wasm.
   *
   * Example: `/vendor/libredwg-web`
   */
  wasmBaseUrl?: string;
  /**
   * Run LibreDWG parsing in a dedicated worker when `workerModuleUrl` or
   * `workerFactory` is available. Defaults to true.
   */
  useWorker?: boolean;
  /**
   * Browser-loadable URL for the ESM entry of `@mlightcad/libredwg-web`.
   * The worker imports this URL without bundling the GPL dependency into core.
   */
  workerModuleUrl?: string;
  /** Create a custom module worker that implements the LibreDWG worker protocol. */
  workerFactory?: () => Worker;
  /** Maximum time allowed for worker parsing. Defaults to 120 seconds. */
  workerTimeoutMs?: number;
}

type DwgPreviewStats = {
  entityCount: number;
  layerCount: number;
  layoutCount: number;
  unknownEntityCount: number;
  visibleLayerCount: number;
  paperSpaceEntityCount: number;
  hasThumbnail: boolean;
};

type DwgSvgBounds = {
  minX: number;
  minY: number;
  width: number;
  height: number;
};

type DwgParseResult = {
  svg: string;
  stats?: DwgPreviewStats;
  thumbnail?: DwgThumbnail;
  conversionError?: string;
};

type DwgWorkerMessage =
  | { type: "progress"; message: string }
  | { type: "success"; result: DwgParseResult }
  | { type: "error"; message: string };

let libreDwgPromise: Promise<LibreDwgModule> | undefined;

const defaultLibreDwgWasmBaseUrl = "/vendor/libredwg-web";
const minReadableDrawingHeight = 420;
const libreDwgPackageName = "@mlightcad/libredwg-web";
const defaultWorkerTimeoutMs = 120_000;
const svgNumberPattern = /-?\d*\.?\d+(?:e[-+]?\d+)?/gi;

export async function renderLibreDwgPreview(
  ctx: CadBinaryPreviewContext,
  options: LibreDwgPreviewOptions = {}
): Promise<PreviewInstance | void> {
  if (ctx.extension !== "dwg" || options.enabled === false) {
    return undefined;
  }

  const shell = document.createElement("div");
  shell.className = "ofv-dwg-preview";
  const status = document.createElement("div");
  status.className = "ofv-dwg-preview-status";
  status.textContent = "Loading DWG rendering engine...";
  shell.append(status);
  ctx.panel.append(shell);

  try {
    const result = await parseDwg(ctx.arrayBuffer, options, ctx.preview.signal, (message) => {
      status.textContent = message;
    });
    const svg = result.svg;
    const stats = result.stats;
    let thumbnailUrl = createDwgThumbnailUrl(result.thumbnail);

    if (result.conversionError) {
      if (thumbnailUrl) {
        const fallbackThumbnailUrl = thumbnailUrl;
        status.replaceChildren(createDwgThumbnailFallbackStatus(ctx.fileName, result.conversionError));
        shell.append(createDwgThumbnailPreview(fallbackThumbnailUrl, ctx.fileName));
        return {
          destroy() {
            URL.revokeObjectURL(fallbackThumbnailUrl);
            shell.remove();
          }
        };
      }
      throw new Error(result.conversionError);
    }

    if (!svg || !/<svg[\s>]/i.test(svg)) {
      if (thumbnailUrl) {
        const fallbackThumbnailUrl = thumbnailUrl;
        status.replaceChildren(createDwgThumbnailFallbackStatus(ctx.fileName, "DWG parser finished but did not produce SVG output."));
        shell.append(createDwgThumbnailPreview(fallbackThumbnailUrl, ctx.fileName));
        return {
          destroy() {
            URL.revokeObjectURL(fallbackThumbnailUrl);
            shell.remove();
          }
        };
      }
      throw new Error("DWG parser finished but did not produce SVG output.");
    }
    if (!stats) {
      throw new Error("DWG parser finished without drawing statistics.");
    }

    const doc = new DOMParser().parseFromString(sanitizeDwgSvgMarkup(svg), "image/svg+xml");
    const svgElement = doc.documentElement;
    if (!(svgElement instanceof SVGElement) || svgElement.nodeName.toLowerCase() !== "svg" || svgElement.querySelector("parsererror")) {
      throw new Error("DWG SVG output is invalid.");
    }

    svgElement.classList.add("ofv-dwg-preview-svg");
    svgElement.setAttribute("role", "img");
    svgElement.setAttribute("aria-label", ctx.fileName);
    sanitizeDwgSvgElement(svgElement);
    normalizeDwgSvg(svgElement);
    status.replaceChildren(createDwgStatusTitle(ctx.fileName, stats));

    const drawing = createDwgDrawingViewport(svgElement);
    if (thumbnailUrl) {
      shell.append(createDwgThumbnailPanel(thumbnailUrl));
    }
    shell.append(drawing.frame);
    return {
      resize() {
        drawing.update();
      },
      canCommand(command) {
        return command === "zoom-in" || command === "zoom-out" || command === "zoom-reset";
      },
      command(command) {
        if (command === "zoom-in") {
          drawing.setZoom(drawing.zoom * 1.18);
          return true;
        }
        if (command === "zoom-out") {
          drawing.setZoom(drawing.zoom / 1.18);
          return true;
        }
        if (command === "zoom-reset") {
          drawing.setZoom(1);
          return true;
        }
        return false;
      },
      destroy() {
        if (thumbnailUrl) {
          URL.revokeObjectURL(thumbnailUrl);
        }
        shell.remove();
      }
    };
  } catch (error) {
    shell.remove();
    if (!isAbortError(error)) {
      console.warn("DWG LibreDWG preview failed, falling back to metadata preview:", error);
    }
    return undefined;
  }
}

function sanitizeDwgSvgMarkup(svg: string): string {
  return svg.replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[\da-f]+);)/gi, "&amp;");
}

function sanitizeDwgSvgElement(svgElement: SVGElement): void {
  svgElement.querySelectorAll("script,style,foreignObject,iframe,object,embed,audio,video").forEach((element) => element.remove());
  for (const element of [svgElement, ...svgElement.querySelectorAll<SVGElement>("*")]) {
    for (const attribute of [...element.attributes]) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim();
      if (
        name.startsWith("on") ||
        ((name === "href" || name === "xlink:href") && value !== "" && !value.startsWith("#")) ||
        (/url\s*\(/i.test(value) && !/url\s*\(\s*["']?#/i.test(value))
      ) {
        element.removeAttribute(attribute.name);
      }
    }
  }
}

async function parseDwg(
  arrayBuffer: ArrayBuffer,
  options: LibreDwgPreviewOptions,
  signal: AbortSignal | undefined,
  onProgress: (message: string) => void
): Promise<DwgParseResult> {
  const canUseWorker =
    options.useWorker !== false &&
    (Boolean(options.workerFactory) || (typeof Worker !== "undefined" && Boolean(options.workerModuleUrl)));
  if (canUseWorker) {
    return parseDwgInWorker(arrayBuffer, options, signal, onProgress);
  }
  onProgress("Parsing DWG on the main thread...");
  return parseDwgOnMainThread(arrayBuffer, options.wasmBaseUrl || defaultLibreDwgWasmBaseUrl, signal);
}

async function parseDwgOnMainThread(
  arrayBuffer: ArrayBuffer,
  wasmBaseUrl: string,
  signal?: AbortSignal
): Promise<DwgParseResult> {
  throwIfAborted(signal);
  const { LibreDwg, Dwg_File_Type } = await loadLibreDwg();
  throwIfAborted(signal);
  const libredwg = await LibreDwg.create(wasmBaseUrl);
  throwIfAborted(signal);
  const data = libredwg.dwg_read_data(arrayBuffer, Dwg_File_Type.DWG);
  if (!data) {
    throw new Error("DWG parser did not return drawing data.");
  }

  try {
    const thumbnail = readDwgThumbnail(libredwg, data);
    try {
      const result = libredwg.convertEx(data);
      const database = result.database;
      return {
        svg: libredwg.dwg_to_svg(database),
        stats: createDwgPreviewStats(database, result.stats.unknownEntityCount, Boolean(thumbnail)),
        thumbnail
      };
    } catch (error) {
      return {
        svg: "",
        thumbnail,
        conversionError: getErrorMessage(error)
      };
    }
  } finally {
    libredwg.dwg_free(data);
  }
}

function parseDwgInWorker(
  arrayBuffer: ArrayBuffer,
  options: LibreDwgPreviewOptions,
  signal: AbortSignal | undefined,
  onProgress: (message: string) => void
): Promise<DwgParseResult> {
  return new Promise((resolve, reject) => {
    let worker: Worker;
    try {
      worker = options.workerFactory?.() ?? createInlineDwgWorker(options.workerModuleUrl!);
    } catch (error) {
      reject(error);
      return;
    }

    let settled = false;
    const timeoutMs = Math.max(1, options.workerTimeoutMs ?? defaultWorkerTimeoutMs);
    const timeoutId = setTimeout(() => {
      finish(() => reject(new Error(`DWG worker timed out after ${timeoutMs}ms.`)));
    }, timeoutMs);

    const onAbort = () => {
      finish(() => reject(createAbortError()));
    };
    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      worker.terminate();
      callback();
    };

    worker.onmessage = (event: MessageEvent<DwgWorkerMessage>) => {
      const message = event.data;
      if (message.type === "progress") {
        onProgress(message.message);
        return;
      }
      if (message.type === "success") {
        finish(() => resolve(normalizeWorkerResult(message.result)));
        return;
      }
      finish(() => reject(new Error(message.message)));
    };
    worker.onerror = (event) => {
      finish(() => reject(new Error(event.message || "DWG worker failed.")));
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    const transferableBuffer = arrayBuffer.slice(0);
    worker.postMessage(
      {
        type: "parse",
        arrayBuffer: transferableBuffer,
        moduleUrl: resolveWorkerAssetUrl(options.workerModuleUrl),
        wasmBaseUrl: resolveWorkerAssetUrl(options.wasmBaseUrl || defaultLibreDwgWasmBaseUrl)
      },
      [transferableBuffer]
    );
  });
}

function resolveWorkerAssetUrl(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const baseUrl = typeof document !== "undefined" ? document.baseURI : globalThis.location?.href;
  return baseUrl ? new URL(value, baseUrl).href : value;
}

function createInlineDwgWorker(moduleUrl: string): Worker {
  if (!moduleUrl) {
    throw new Error("DWG worker requires libreDwg.workerModuleUrl or a custom workerFactory.");
  }
  const source = `(${runLibreDwgWorker.toString()})();`;
  const blobUrl = URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
  try {
    return new Worker(blobUrl, { type: "module", name: "open-file-viewer-dwg" });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

function runLibreDwgWorker(): void {
  const scope = self as unknown as {
    onmessage: ((event: MessageEvent) => void) | null;
    postMessage: (message: unknown, transfer?: Transferable[]) => void;
  };
  const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error || "Unknown DWG error"));
  const copyThumbnail = (thumbnail: { data?: Uint8Array; type?: number } | null | undefined) => {
    if (!thumbnail?.data?.length) {
      return undefined;
    }
    const data = thumbnail.data.slice();
    return { data: data.buffer, type: Number(thumbnail.type || 0) };
  };

  scope.onmessage = async (event) => {
    const request = event.data as {
      type?: string;
      arrayBuffer?: ArrayBuffer;
      moduleUrl?: string;
      wasmBaseUrl?: string;
    };
    if (request.type !== "parse" || !request.arrayBuffer || !request.moduleUrl) {
      scope.postMessage({ type: "error", message: "DWG worker received an invalid parse request." });
      return;
    }

    try {
      scope.postMessage({ type: "progress", message: "Loading DWG rendering engine in worker..." });
      const importModule = new Function("url", "return import(url)") as (url: string) => Promise<{
        Dwg_File_Type: { DWG: number };
        LibreDwg: { create(filepath?: string): Promise<any> };
      }>;
      const module = await importModule(request.moduleUrl);
      const libredwg = await module.LibreDwg.create(request.wasmBaseUrl);
      scope.postMessage({ type: "progress", message: "Parsing DWG geometry in worker..." });
      const data = libredwg.dwg_read_data(request.arrayBuffer, module.Dwg_File_Type.DWG);
      if (!data) {
        throw new Error("DWG parser did not return drawing data.");
      }

      try {
        let thumbnail;
        try {
          thumbnail = copyThumbnail(libredwg.dwg_bmp(data));
        } catch {
          thumbnail = undefined;
        }
        try {
          const converted = libredwg.convertEx(data);
          const database = converted.database;
          const layers = database.tables?.LAYER?.entries ?? [];
          const entities = database.entities ?? [];
          const layouts = database.objects?.LAYOUT ?? [];
          const result = {
            svg: libredwg.dwg_to_svg(database),
            stats: {
              entityCount: entities.length,
              layerCount: layers.length,
              layoutCount: layouts.length,
              unknownEntityCount: Number(converted.stats?.unknownEntityCount || 0),
              visibleLayerCount: layers.filter((layer: { off?: boolean; frozen?: boolean }) => !layer.off && !layer.frozen).length,
              paperSpaceEntityCount: entities.filter((entity: { isInPaperSpace?: boolean }) => entity.isInPaperSpace).length,
              hasThumbnail: Boolean(thumbnail)
            },
            thumbnail
          };
          const transfer = thumbnail?.data ? [thumbnail.data] : [];
          scope.postMessage({ type: "success", result }, transfer);
        } catch (error) {
          const result = {
            svg: "",
            thumbnail,
            conversionError: errorMessage(error)
          };
          const transfer = thumbnail?.data ? [thumbnail.data] : [];
          scope.postMessage({ type: "success", result }, transfer);
        }
      } finally {
        libredwg.dwg_free(data);
      }
    } catch (error) {
      scope.postMessage({ type: "error", message: errorMessage(error) });
    }
  };
}

function normalizeWorkerResult(result: DwgParseResult): DwgParseResult {
  const thumbnail = result.thumbnail as DwgThumbnail | { data: ArrayBuffer; type: number } | undefined;
  return {
    ...result,
    thumbnail: thumbnail
      ? {
          type: thumbnail.type,
          data: thumbnail.data instanceof Uint8Array ? thumbnail.data : new Uint8Array(thumbnail.data)
        }
      : undefined
  };
}

function createAbortError(): Error {
  const error = new Error("DWG preview was aborted.");
  error.name = "AbortError";
  return error;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error || "Unknown DWG error");
}

function loadLibreDwg(): Promise<LibreDwgModule> {
  libreDwgPromise ||= importOptionalModule<LibreDwgModule>(libreDwgPackageName);
  return libreDwgPromise;
}

function importOptionalModule<T>(packageName: string): Promise<T> {
  return new Function("packageName", "return import(packageName)")(packageName) as Promise<T>;
}

function createDwgStatusTitle(fileName: string, stats: DwgPreviewStats): HTMLElement {
  const wrapper = document.createElement("span");
  const title = document.createElement("strong");
  const note = document.createElement("small");
  title.textContent = `实验性 DWG 模型空间预览 · ${fileName}`;
  note.textContent = [
    `${stats.entityCount.toLocaleString()} 个实体`,
    `${stats.visibleLayerCount}/${stats.layerCount} 个可见图层`,
    `${stats.layoutCount} 个布局`,
    stats.paperSpaceEntityCount ? `${stats.paperSpaceEntityCount.toLocaleString()} 个图纸空间实体` : "模型空间线稿",
    stats.unknownEntityCount ? `${stats.unknownEntityCount} 个未知实体` : "实体解析完整",
    stats.hasThumbnail ? "包含内置缩略图" : "无内置缩略图"
  ].join(" · ");
  const warning = document.createElement("small");
  warning.textContent = "当前为 LibreDWG WASM 线稿预览，复杂布局/打印空间/字体/填充与专业 CAD 仍可能存在差异。";
  wrapper.append(title, note, warning);
  return wrapper;
}

function createDwgThumbnailFallbackStatus(fileName: string, error: unknown): HTMLElement {
  const wrapper = document.createElement("span");
  const title = document.createElement("strong");
  title.textContent = `DWG 内置预览图 · ${fileName}`;
  const note = document.createElement("small");
  note.textContent = "LibreDWG 已读取到文件内置缩略图，但线稿预览未能生成，已自动切换为缩略图兜底。";
  const detail = document.createElement("small");
  detail.textContent = error instanceof Error ? error.message : String(error || "未知解析错误");
  wrapper.append(title, note, detail);
  return wrapper;
}

function createDwgPreviewStats(database: DwgDatabase, unknownEntityCount: number, hasThumbnail: boolean): DwgPreviewStats {
  const layers = database.tables.LAYER.entries;
  return {
    entityCount: database.entities.length,
    layerCount: layers.length,
    layoutCount: database.objects.LAYOUT.length,
    unknownEntityCount,
    visibleLayerCount: layers.filter((layer) => !layer.off && !layer.frozen).length,
    paperSpaceEntityCount: database.entities.filter((entity) => entity.isInPaperSpace).length,
    hasThumbnail
  };
}

function readDwgThumbnail(libredwg: LibreDwgValue, data: number): DwgThumbnail | undefined {
  try {
    const thumbnail = libredwg.dwg_bmp(data);
    if (!thumbnail?.data?.length) {
      return undefined;
    }
    return thumbnail;
  } catch {
    return undefined;
  }
}

function createDwgThumbnailUrl(thumbnail: DwgThumbnail | undefined): string | undefined {
  if (!thumbnail) {
    return undefined;
  }
  if (thumbnail.type === 6) {
    return URL.createObjectURL(new Blob([toArrayBuffer(thumbnail.data)], { type: "image/png" }));
  }
  if (thumbnail.type === 2) {
    return URL.createObjectURL(new Blob([toArrayBuffer(createBmpFileBytes(thumbnail.data))], { type: "image/bmp" }));
  }
  return undefined;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function createBmpFileBytes(dibBytes: Uint8Array): Uint8Array {
  const view = new DataView(dibBytes.buffer, dibBytes.byteOffset, dibBytes.byteLength);
  const headerSize = view.getUint32(0, true);
  const bitCount = view.getUint16(14, true);
  const paletteBytes = bitCount <= 8 ? 2 ** bitCount * 4 : 0;
  const pixelOffset = 14 + headerSize + paletteBytes;
  const bytes = new Uint8Array(14 + dibBytes.byteLength);
  bytes[0] = 0x42;
  bytes[1] = 0x4d;
  const fileView = new DataView(bytes.buffer);
  fileView.setUint32(2, bytes.byteLength, true);
  fileView.setUint32(10, pixelOffset, true);
  bytes.set(dibBytes, 14);
  return bytes;
}

function createDwgThumbnailPanel(thumbnailUrl: string): HTMLElement {
  const panel = document.createElement("figure");
  panel.className = "ofv-dwg-thumbnail";
  const image = document.createElement("img");
  image.src = thumbnailUrl;
  image.alt = "DWG 文件内置缩略图";
  const caption = document.createElement("figcaption");
  caption.textContent = "文件内置缩略图";
  panel.append(image, caption);
  return panel;
}

function createDwgThumbnailPreview(thumbnailUrl: string, fileName: string): HTMLElement {
  const figure = document.createElement("figure");
  figure.className = "ofv-dwg-thumbnail-preview";
  const image = document.createElement("img");
  image.src = thumbnailUrl;
  image.alt = `${fileName} 内置预览图`;
  const caption = document.createElement("figcaption");
  caption.textContent = "DWG 文件内置预览图。若需要接近 CAD 布局/打印空间的高保真效果，请使用同名 PNG、SVG、PDF 导出图，或通过 binaryRenderer 接入专业 CAD 渲染/转换服务。";
  figure.append(image, caption);
  return figure;
}

function normalizeDwgSvg(svgElement: SVGElement): void {
  svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");
  removeInheritedDwgFills(svgElement);
  focusDwgSvgOnMainDrawing(svgElement);
  const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  style.textContent = `
    .ofv-dwg-preview-svg { background: #020617; }
    .ofv-dwg-preview-svg g {
      fill: none !important;
    }
    .ofv-dwg-preview-svg line,
    .ofv-dwg-preview-svg path,
    .ofv-dwg-preview-svg polyline,
    .ofv-dwg-preview-svg polygon,
    .ofv-dwg-preview-svg circle,
    .ofv-dwg-preview-svg ellipse,
    .ofv-dwg-preview-svg rect {
      fill: none !important;
      vector-effect: non-scaling-stroke;
      stroke-width: 0.7px !important;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .ofv-dwg-preview-svg [stroke="rgb(0,255,0)"] {
      stroke: #34d399 !important;
      stroke-opacity: 0.58 !important;
    }
    .ofv-dwg-preview-svg text {
      vector-effect: non-scaling-stroke;
      stroke-width: 0 !important;
      fill: currentColor !important;
    }
  `;
  svgElement.prepend(style);
}

function removeInheritedDwgFills(svgElement: SVGElement): void {
  const shapeSelector = "line,path,polyline,polygon,circle,ellipse,rect";
  for (const group of svgElement.querySelectorAll<SVGGElement>("g[fill]")) {
    group.setAttribute("fill", "none");
  }
  for (const styledElement of svgElement.querySelectorAll<SVGElement>("[style]")) {
    styledElement.style.fill = "none";
  }
  for (const shape of svgElement.querySelectorAll<SVGElement>(shapeSelector)) {
    shape.setAttribute("fill", "none");
  }
}

function focusDwgSvgOnMainDrawing(svgElement: SVGElement): void {
  const originalBounds = readSvgViewBox(svgElement);
  const mainBounds = estimateMainDrawingBounds(svgElement);
  if (!originalBounds || !mainBounds || !shouldUseMainDrawingBounds(originalBounds, mainBounds)) {
    return;
  }

  const paddedBounds = padBounds(mainBounds, 0.05);
  svgElement.dataset.originalViewBox = formatViewBox(originalBounds);
  svgElement.dataset.focusViewBox = formatViewBox(paddedBounds);
  svgElement.setAttribute("viewBox", formatViewBox(paddedBounds));
}

function shouldUseMainDrawingBounds(original: DwgSvgBounds, candidate: DwgSvgBounds): boolean {
  const originalAspectRatio = original.width / original.height;
  const candidateAspectRatio = candidate.width / candidate.height;
  const originalArea = original.width * original.height;
  const candidateArea = candidate.width * candidate.height;
  if (!Number.isFinite(originalArea) || !Number.isFinite(candidateArea) || candidateArea <= 0) {
    return false;
  }

  return (
    originalAspectRatio > 8 ||
    originalAspectRatio < 0.125 ||
    candidateArea / originalArea < 0.55 ||
    Math.abs(Math.log(originalAspectRatio / candidateAspectRatio)) > Math.log(4)
  );
}

function estimateMainDrawingBounds(svgElement: SVGElement): DwgSvgBounds | undefined {
  const points: Array<[number, number]> = [];
  const addPoint = (x: number, y: number) => {
    if (Number.isFinite(x) && Number.isFinite(y) && Math.abs(x) < 1_000_000_000 && Math.abs(y) < 1_000_000_000) {
      points.push([x, y]);
    }
  };

  for (const element of svgElement.querySelectorAll<SVGGeometryElement>("line,path,polyline,polygon,circle,ellipse,rect,text,use")) {
    if (element.closest("defs")) {
      continue;
    }
    collectElementPoints(element, addPoint);
  }

  if (points.length < 16) {
    return undefined;
  }

  const xs = points.map(([x]) => x).sort((a, b) => a - b);
  const ys = points.map(([, y]) => y).sort((a, b) => a - b);
  const minX = quantile(xs, 0.005);
  const maxX = quantile(xs, 0.995);
  const minY = quantile(ys, 0.005);
  const maxY = quantile(ys, 0.995);
  if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
    return undefined;
  }

  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) {
    return undefined;
  }

  return { minX, minY, width, height };
}

function collectElementPoints(element: SVGElement, addPoint: (x: number, y: number) => void): void {
  const tagName = element.tagName.toLowerCase();
  if (tagName === "line") {
    addPoint(readNumberAttribute(element, "x1"), readNumberAttribute(element, "y1"));
    addPoint(readNumberAttribute(element, "x2"), readNumberAttribute(element, "y2"));
  } else if (tagName === "circle") {
    const cx = readNumberAttribute(element, "cx");
    const cy = readNumberAttribute(element, "cy");
    const r = readNumberAttribute(element, "r");
    addPoint(cx - r, cy - r);
    addPoint(cx + r, cy + r);
  } else if (tagName === "ellipse") {
    const cx = readNumberAttribute(element, "cx");
    const cy = readNumberAttribute(element, "cy");
    const rx = readNumberAttribute(element, "rx");
    const ry = readNumberAttribute(element, "ry");
    addPoint(cx - rx, cy - ry);
    addPoint(cx + rx, cy + ry);
  } else if (tagName === "rect") {
    const x = readNumberAttribute(element, "x");
    const y = readNumberAttribute(element, "y");
    addPoint(x, y);
    addPoint(x + readNumberAttribute(element, "width"), y + readNumberAttribute(element, "height"));
  } else if (tagName === "text") {
    addPoint(readNumberAttribute(element, "x"), readNumberAttribute(element, "y"));
  } else if (tagName === "path") {
    collectNumberPairs(element.getAttribute("d"), addPoint);
  } else if (tagName === "polyline" || tagName === "polygon") {
    collectNumberPairs(element.getAttribute("points"), addPoint);
  }

  collectTranslatePoints(element.getAttribute("transform"), addPoint);
}

function collectNumberPairs(value: string | null, addPoint: (x: number, y: number) => void): void {
  if (!value) {
    return;
  }

  const numbers = readNumbers(value);
  for (let index = 0; index + 1 < numbers.length; index += 2) {
    addPoint(numbers[index], numbers[index + 1]);
  }
}

function collectTranslatePoints(value: string | null, addPoint: (x: number, y: number) => void): void {
  if (!value) {
    return;
  }

  const translatePattern = /translate\(\s*(-?\d*\.?\d+(?:e[-+]?\d+)?)(?:[\s,]+(-?\d*\.?\d+(?:e[-+]?\d+)?))?/gi;
  for (const match of value.matchAll(translatePattern)) {
    addPoint(Number(match[1]), Number(match[2] ?? 0));
  }
}

function readNumbers(value: string): number[] {
  const matches = value.match(svgNumberPattern);
  return matches ? matches.map((item) => Number(item)).filter(Number.isFinite) : [];
}

function readNumberAttribute(element: SVGElement, name: string): number {
  const value = Number(element.getAttribute(name) ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function quantile(values: number[], ratio: number): number {
  const index = Math.max(0, Math.min(values.length - 1, Math.floor((values.length - 1) * ratio)));
  return values[index];
}

function padBounds(bounds: DwgSvgBounds, ratio: number): DwgSvgBounds {
  const paddingX = bounds.width * ratio;
  const paddingY = bounds.height * ratio;
  return {
    minX: bounds.minX - paddingX,
    minY: bounds.minY - paddingY,
    width: bounds.width + paddingX * 2,
    height: bounds.height + paddingY * 2
  };
}

function formatViewBox(bounds: DwgSvgBounds): string {
  return [bounds.minX, bounds.minY, bounds.width, bounds.height].map((value) => Number(value.toFixed(4))).join(" ");
}

function createDwgDrawingViewport(svgElement: SVGElement) {
  const frame = document.createElement("div");
  frame.className = "ofv-dwg-preview-frame";
  const importedSvg = document.importNode(svgElement, true);
  frame.append(importedSvg);
  const aspectRatio = readSvgAspectRatio(svgElement);
  let zoom = 1;

  const update = () => {
    const frameWidth = Math.max(frame.clientWidth, 1);
    const readableWidth = aspectRatio ? minReadableDrawingHeight * aspectRatio : frameWidth;
    const width = Math.max(frameWidth, readableWidth) * zoom;
    importedSvg.style.width = `${Math.round(width)}px`;
    importedSvg.style.minWidth = `${Math.round(width)}px`;
  };

  const setZoom = (nextZoom: number) => {
    zoom = Math.min(Math.max(nextZoom, 0.2), 8);
    update();
  };

  requestAnimationFrame(update);

  return {
    frame,
    get zoom() {
      return zoom;
    },
    setZoom,
    update
  };
}

function readSvgAspectRatio(svgElement: SVGElement): number | undefined {
  const viewBox = readSvgViewBox(svgElement);
  return viewBox ? viewBox.width / viewBox.height : undefined;
}

function readSvgViewBox(svgElement: SVGElement): DwgSvgBounds | undefined {
  const viewBox = svgElement.getAttribute("viewBox");
  if (!viewBox) {
    return undefined;
  }

  const [minX, minY, width, height] = viewBox
    .trim()
    .split(/[\s,]+/)
    .map((value) => Number(value));
  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return undefined;
  }

  return { minX, minY, width, height };
}
